import { useCallback } from "react";

/**
 * Gives every request a real deadline (sc-100769).
 *
 * The shared api-client sets no fetch timeout and passes no `AbortSignal` of its own, so a request that
 * never *settles* never reaches its caller's `finally` — and every piece of state that `finally` owns is
 * stranded: the in-flight guard, the loading flag, a row's pending marker, a dialog's submitting flag.
 * Three code-review rounds patched those symptoms one at a time with generation counters and elapsed-time
 * checks; each patch left another owner unattended, because *simulating* abandonment never makes the
 * promise settle. Cancelling does: an aborted fetch rejects, the `finally` runs, and every ordinary
 * ownership path works again. [code review r3]
 *
 * Also bounds request *accumulation*: without cancellation each retry adds another genuinely in-flight
 * request, which is what the acceptance criterion "a slow response must not stack up" forbids.
 *
 * **Deliberately does NOT abort on unmount.** It looked like free hygiene and it was a dev-breaking
 * regression: the app mounts under `StrictMode` (`main.tsx`), whose effect replay runs cleanup and setup
 * again on the same fiber, so refs persist. An unmount abort killed the first mount GET, the replayed
 * `load()` hit a caller's still-true single-flight guard and issued nothing, and the aborted request's
 * `finally` then released the guard over an empty table — `pnpm dev:conduit-admin` showed "No recurring
 * jobs registered." with no error. The timeout is the bound that matters; a request outliving its
 * component simply resolves into no-op setState calls. [code review r4]
 */
export function useBoundedRequest(timeoutMs: number): {
  /** Runs `call` with a signal that aborts after `timeoutMs`. Rejects with an AbortError on timeout. */
  run: <T>(call: (signal: AbortSignal) => Promise<T>) => Promise<T>;
} {
  const run = useCallback(
    async <T,>(call: (signal: AbortSignal) => Promise<T>): Promise<T> => {
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), timeoutMs);
      try {
        return await call(controller.signal);
      } finally {
        window.clearTimeout(timer);
      }
    },
    [timeoutMs]
  );

  return { run };
}

/** True for the rejection an aborted request produces, which callers must not report as a failure. */
export function isAbortError(err: unknown): boolean {
  return err instanceof Error && err.name === "AbortError";
}
